import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { formatClassName, pickDataAttributes } from '../Util';
import ConstructionCard from './ConstructionCard';
import ExplainerText from './ExplainerText';
import Button from './Button';
import Icon from './Icon';
import DisplayWidthForCards from './Layout/Display/DisplayWidthForCards';
import i18n from '../i18n/translate';
import moment from 'moment';

const WizardCards = props => {
  const { saveBtnLabel, stepCardProps = {}, steps = [], taskCardProps } = props;
  const [activeStep, setActiveStep] = useState(props.initialStep);
  const allTasksCompleted = steps.every(step => step.tasks && step.tasks.every(task => task.completed_at));

  const isFirstStep = position => position === 0;
  const isLastStep = position => position === steps.length - 1;

  const handleCompleteTask = task => {
    if (task.onComplete) task.onComplete();

    props.onCompleteTask(task);
  }

  const getStepContent = step => {
    if (!step.tasks) {
      return step.content;
    }

    return step.tasks.map(task => {


      return (
        <ConstructionCard
          {...taskCardProps}
          key={task.title}
          title={task.title}
          expanded={true}
          className={formatClassName(['wizard-task-card', props.stepClassName])}
          attentionGrabber={task.completed_at ? { label: i18n.t('Complete'), type: 'success' } : null}
        >
          <ExplainerText>
            {task.description}
            {!!task.learnMoreUrl && <a href={task.learnMoreUrl} className="m-l-5" target="_blank">{i18n.t('Learn More')}</a>}
          </ExplainerText>
          {task.completed_at && (
            <div className="task-complete-info">
              <Icon iconType="check" additionalClasses="m-r-5" />
              {i18n.t('Marked Complete by %s on %s', {
                postProcess: 'sprintf',
                sprintf: [task.user, moment(task.completed_at || Date.now()).format('LL')]
              })}
              <Button style="link" icon="replay" onClick={() => props.onUndoTask(task)} label={i18n.t('Undo')} className="m-l-10" />
            </div>
          )}
          <div className="task-buttons">
            <Button style="secondary" href={task.goToUrl} label={task.goToLabel} className="btn-goto-task" />
            {!task.completed_at && (
              <Button
                style="secondary"
                onClick={() => handleCompleteTask(task)}
                icon="check"
                label={i18n.t('Mark Task Complete')}
                className="btn-complete-task"
              />
            )}
          </div>
        </ConstructionCard>
      );
    });
  }

  const getStepNavButtons = position => (
    <div className="wizard-nav-buttons">
      {!isFirstStep(position) && (
        <Button
          style="link"
          label={i18n.t('Previous Step')}
          icon="arrow-back"
          className="btn-previous"
          onClick={() => navigateStep('backwards', position)}
        />
      )}
      {!isLastStep(position) && (
        <Button
          label={i18n.t('Next Step')}
          rightIcon="arrow-forward"
          className="btn-next"
          onClick={() => navigateStep('forward', position)}
        />
      )}
    </div>
  );

  const navigateStep = (direction, position) => {
    // since steps can be expanded when out of focus, we need to set boundaries for movement
    const index = activeStep !== position ? position : activeStep;
    const noAction = (isFirstStep(index) && direction === 'backwards') || (isLastStep(index) && direction === 'forward');
    const movement = noAction ? 0 : 1;
    const newStep = direction === 'backwards' ? index - movement : index + movement;

    setActiveStep(newStep);
  }

  const onExpandedToggle = (index, expanded) => {
    if(expanded) {
      setActiveStep(index);
    }
  }

  return (
    <div {...pickDataAttributes(props)} className={formatClassName(['wizard-cards', props.className])}>
      <DisplayWidthForCards>
        {steps.map((step, index) => (
          <ConstructionCard
            {...stepCardProps}
            key={index}
            className={formatClassName(['wizard-step-card', props.stepClassName])}
            title={step.title}
            onExpandedToggle={(expanded) => onExpandedToggle(index, expanded)}
            expanded={activeStep === index}
            headerImageUrl={step.imageUrl}
          >
            <ExplainerText>
              {step.description}
            </ExplainerText>

            {getStepContent(step)}
            {getStepNavButtons(index)}
          </ConstructionCard>
        ))}
        <div className={formatClassName(['wizard-cards-actions', stepCardProps.size])}>
          <Button label={saveBtnLabel} onClick={props.onFinish} className="btn-finish" disabled={!allTasksCompleted} />
        </div>
      </DisplayWidthForCards>
    </div>
  );
};

WizardCards.propTypes = {
  className: PropTypes.string,
  initialStep: PropTypes.number,
  onCompleteTask: PropTypes.func,
  onFinish: PropTypes.func,
  onUndoTask: PropTypes.func,
  saveBtnLabel: PropTypes.string,
  stepCardProps: ConstructionCard.propTypes,
  stepClassName: PropTypes.string,
  steps: PropTypes.arrayOf(PropTypes.shape({
    content: PropTypes.node,
    description: PropTypes.string,
    imageUrl: PropTypes.string,
    tasks: PropTypes.shape({
      complete: PropTypes.bool,
      completed_at: PropTypes.string,
      description: PropTypes.string,
      goToLabel: PropTypes.string,
      goToUrl: PropTypes.string,
      learnMoreUrl: PropTypes.string,
      onComplete: PropTypes.func,
      title: PropTypes.string,
      user: PropTypes.string
    }),
    title: PropTypes.string
  })).isRequired,
  taskCardProps: ConstructionCard.propTypes,
  taskClassName: PropTypes.string
};

WizardCards.defaultProps = {
  initialStep: 0,
  saveBtnLabel: i18n.t('Save'),
  user: 'Admin'
};

export default WizardCards;
